/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Use cases section with accordion-style tabs and image display for AI Image Enhancer
 */
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

interface UseCase {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'ecommerce',
    title: 'E-Commerce',
    description:
      'Online sellers upgrade product photos so listings look more professional and trustworthy. Clearer images help customers understand details faster and feel more confident about buying. Better visuals lead to stronger engagement and higher conversion rates.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/ecommerce-product-image.webp',
  },
  {
    id: 'marketing-branding',
    title: 'Marketing & Branding',
    description:
      'Brand teams refresh logos, icons, and graphics that lose quality when resized. The enhancer restores crisp edges and smooth textures, keeping brand visuals sharp across websites, ads, and social posts. Consistent image quality helps campaigns look polished on every screen.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/marketing-branding-image.webp',
  },
  {
    id: 'real-estate',
    title: 'Real Estate',
    description:
      'Real estate agents improve property photos that need to stand out at first glance. The AI boosts clarity, sharpens details, and restores color so rooms look brighter and more inviting. Stronger visuals help attract more viewers and increase interest in each listing.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/real-estate-interior-image.webp',
  },
  {
    id: 'designer-artist',
    title: 'Designer and Artist',
    description:
      'Creators upscale anime frames, illustrations, and digital artwork to 4K or 8K. Fine lines become clearer and colors gain depth, making each piece ready for prints, posters, or online sharing. High-resolution results help creative work look clean and professional.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/designer-artwork-image.webp',
  },
  {
    id: 'photography',
    title: 'Photography',
    description:
      'Photographers fix blurry shots, restore old images, and prepare photos for high-quality prints. The AI improves resolution and clarity automatically, saving hours of manual editing. Cleaner, sharper images help photographers deliver professional results faster.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/portrait-photography-image.webp',
  },
];

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#6366F1] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeUseCase = useCases[activeIndex];

  // 预加载所有图片
  const imageUrls = useMemo(() => useCases.map((uc) => uc.imageUrl), []);
  useImagePreload(imageUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Best AI Photo Enhancer for Diverse Occasions
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut adapts to the needs of photographers, sellers, artists, and everyday
          users. No matter your project, it delivers refined, lifelike visuals that make
          your work stand out.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Image */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
              <Image
                src={activeUseCase.imageUrl}
                alt={activeUseCase.title}
                fill
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
